import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Banknote, Briefcase, CalendarCheck, Mail, MapPin, Star, Wallet } from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { Button } from '../components/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/Tabs';
import { EmiratiBadge } from '../components/EmiratiBadge';
import { StatTile } from '../components/StatTile';
import { OverviewPanel } from '../components/OverviewPanel';
import { SocialInsightPanel } from '../components/SocialInsightPanel';
import { EventsTable } from '../components/EventsTable';
import { PaymentsPanel } from '../components/PaymentsPanel';
import { TierBadge } from '../components/TierBadge';
import { ExpertTierPanel } from '../components/ExpertTierPanel';
import { experts } from '../data/experts';
import { getTier } from '../utils/tier';
import { averageRating, engagementCount, formatAED, getInitials, totalPaid } from '../utils/expert';
export function ExpertProfile() {
  const {
    id
  } = useParams();
  const expert = experts.find(e => e.id === id);
  if (!expert) {
    return <main className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center" data-id="element-775" data-mp="3u7tyfg9bbm0x">
        <p className="text-sm font-medium" data-id="element-776" data-mp="cv9e22u6treq">Expert not found</p>
        <p className="mt-1 text-xs text-muted-foreground" data-id="element-777" data-mp="7x7y3qs2687h">This profile may have been removed from the directory.</p>
        <Link to="/" className="mt-4 inline-flex h-7 items-center rounded-md border px-3 text-xs font-medium hover:bg-muted" data-id="element-778" data-mp="lwfxoks4vic7">
          Back to directory
        </Link>
      </main>;
  }
  const rating = averageRating(expert);
  const upcoming = expert.events.filter(e => e.status === 'Upcoming').length;
  return <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-id="element-779" data-mp="14o8nk6bwonfp">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-id="element-780" data-mp="14mfekkdtlr4j">
        <ArrowLeft className="size-3.5" aria-hidden="true" data-id="element-781" data-mp="17q4vsrwl5wdj" /> Expert directory
      </Link>

      <section className="mt-4 flex flex-col gap-5 md:flex-row md:items-start md:justify-between" data-id="element-782" data-mp="17oa7r4q0po58">
        <div className="flex items-start gap-4" data-id="element-783" data-mp="1cmvbvf8e4ryy">
          <Avatar className="size-16 text-lg" data-id="element-784" data-mp="1hn9g0m6vfhrp">
            <AvatarFallback className="font-medium" data-id="element-785" data-mp="1hmgb7qrh32c7">{getInitials(expert.name)}</AvatarFallback>
          </Avatar>
          <div data-id="element-786" data-mp="1qguwqb2lj93r">
            <div className="flex flex-wrap items-center gap-2" data-id="element-787" data-mp="1qhe44xumef3j">
              <h1 className="text-2xl font-semibold tracking-tight" data-id="element-788" data-mp="1qiywqhxdjnyr">{expert.name}</h1>
              {expert.isEmirati && <EmiratiBadge data-id="element-789" data-mp="20esj0t0wavtq" />}
              <TierBadge tier={getTier(expert)} data-id="element-790" data-mp="20eitt7nocsp3" />
            </div>
            <p className="mt-1 text-sm text-muted-foreground" data-id="element-791" data-mp="25dvd5ifc7hmx">{expert.title}</p>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-id="element-792" data-mp="25bsp33xqvyku">
              <span className="inline-flex items-center gap-1" data-id="element-793" data-mp="6342wnixgn7r"><Briefcase className="size-3" aria-hidden="true" data-id="element-794" data-mp="b0vqx6ggbp6o" />{expert.organization}</span>
              <span className="inline-flex items-center gap-1" data-id="element-795" data-mp="2oadumdemgl2l"><MapPin className="size-3" aria-hidden="true" data-id="element-796" data-mp="2oc8ahdb1dmzt" />{expert.location}, UAE</span>
              <span data-id="element-797" data-mp="2jhautnj94fdy">Availability: <span className="font-medium text-foreground" data-id="element-798" data-mp="2fhto6qs39c77">{expert.availability}</span></span>
            </p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-799" data-mp="2ajih2nt6hfso">
          <Button variant="outline" data-id="element-800" data-mp="2akjzjdcix4kf"><Mail data-id="element-801" data-mp="378zrfg33vq0p" /> Contact</Button>
          <Button disabled={expert.availability === 'Booked'} data-id="element-802" data-mp="377yh5dtwz7k1">
            <CalendarCheck data-id="element-803" data-mp="337ejm95fi1xd" /> {expert.availability === 'Booked' ? 'Fully booked' : 'Request engagement'}
          </Button>
        </div>
      </section>

      <section aria-label="Key metrics" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-804" data-mp="2y708ujcg0dvn">
        <StatTile label="Engagements" value={String(engagementCount(expert))} hint={`${upcoming} upcoming`} icon={CalendarCheck} data-id="element-805" data-mp="2t8fakpgr449u" />
        <StatTile label="Review score" value={`${rating.toFixed(1)} / 5`} hint={`${expert.reviews.length} reviews`} icon={Star} data-id="element-806" data-mp="1mr1wa5i239po" />
        <StatTile label="Day rate" value={formatAED(expert.dailyRate)} hint={`Half day ${formatAED(expert.halfDayRate)}`} icon={Wallet} data-id="element-807" data-mp="35qzqnqz74bwf" />
        <StatTile label="Paid to date" value={formatAED(totalPaid(expert))} hint="Settled by BAK" icon={Banknote} data-id="element-808" data-mp="3ai9h2tskjj9d" />
      </section>

      <Tabs defaultValue="overview" className="mt-8" data-id="element-809" data-mp="2wo8etq2ere49">
        <TabsList data-id="element-810" data-mp="31q7iz25z2f29">
          <TabsTrigger value="overview" data-id="element-811" data-mp="31pnxxcl0zak0">Overview</TabsTrigger>
          <TabsTrigger value="tier" data-id="element-812" data-mp="3peh0hrw5kgnl">Tier & privileges</TabsTrigger>
          <TabsTrigger value="social" data-id="element-813" data-mp="3pcw2fsamqfkz">Social & AI insight</TabsTrigger>
          <TabsTrigger value="events" data-id="element-814" data-mp="3tfj22fdjl4sq">Events ({expert.events.length})</TabsTrigger>
          <TabsTrigger value="payments" data-id="element-815" data-mp="3ffri7y0cgu6t">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4" data-id="element-816" data-mp="3keum3f4l8ud7">
          <OverviewPanel expert={expert} data-id="element-817" data-mp="3khgtnerhb9vf" />
        </TabsContent>
        <TabsContent value="tier" className="mt-4" data-id="element-818" data-mp="240m9ac9mdzer">
          <ExpertTierPanel expert={expert} data-id="element-819" data-mp="2907ciyvtotxa" />
        </TabsContent>
        <TabsContent value="social" className="mt-4" data-id="element-820" data-mp="291s3yfmubwqx">
          <SocialInsightPanel social={expert.social} data-id="element-821" data-mp="21g6gikbp67u3" />
        </TabsContent>
        <TabsContent value="events" className="mt-4" data-id="element-822" data-mp="1wi4ym2q0cek7">
          <EventsTable events={expert.events} data-id="element-823" data-mp="1whd5qznsouxm" />
        </TabsContent>
        <TabsContent value="payments" className="mt-4" data-id="element-824" data-mp="2ahw57ie72so6">
          <PaymentsPanel expert={expert} data-id="element-825" data-mp="2afa0rrmlota6" />
        </TabsContent>
      </Tabs>
    </main>;
}